import type { Dispatch, PropsWithChildren, ReactElement } from 'react';
import { useReducer, createContext, useContext, useEffect } from 'react';
import { mapReducer, type MapState, type MapStateAction } from './reducers/mapReducer';
import localStore from '../lib/storage/localStore';

import type { CustomMarkersBySubEvent } from '../views/following/components/custom-markers/types';

const defaultState: MapState = {
  customMarkers: {},
  map: undefined,
  route: undefined,
  routeInfoWindow: 'none',
};

type MapStateProviderCtx = [MapState, Dispatch<MapStateAction>];

const MapCtx = createContext<MapStateProviderCtx>([defaultState, () => null]);

const MapProvider = ({ children }: PropsWithChildren): ReactElement => {
  const [state, dispatch] = useReducer(mapReducer, defaultState);
  const { customMarkers } = state;

  // Load custom markers from local storage
  useEffect(() => {
    localStore.getItem<CustomMarkersBySubEvent>('customMarkers').then((value) => {
      if (value !== null) {
        dispatch({ type: 'setCustomMarkers', payload: value as CustomMarkersBySubEvent });
      }
    });
  }, []);

  // Save custom markers to local storage
  useEffect(() => {
    localStore.setItem('customMarkers', customMarkers);
  }, [customMarkers]);

  return <MapCtx.Provider value={[state, dispatch]}>{children}</MapCtx.Provider>;
};

export const useMap = (): MapStateProviderCtx => useContext<MapStateProviderCtx>(MapCtx);

export default MapProvider;
